import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { HelpCircle, Play, RotateCcw } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTourStore } from '../store/tour-store';
import { EnLaRanuraDeAyuda } from '@/components/layout/ranura-de-la-barra';
import type { TourConfig, TourLevel } from '../types/tour.types';

interface TourFabProps {
  tourConfig: TourConfig | null;
  pendingCount: number;
}

const LEVELS: { value: TourLevel; label: string; desc: string }[] = [
  { value: 'basico', label: 'Básico', desc: 'Lo esencial para empezar' },
  { value: 'intermedio', label: 'Intermedio', desc: 'Funciones más avanzadas' },
  { value: 'avanzado', label: 'Avanzado', desc: 'Todo lo que necesitas saber' },
];

export function TourFab({ tourConfig, pendingCount }: TourFabProps) {
  const [open, setOpen] = useState(false);
  const { selectedLevel, setLevel, startTour, isActive, isTourCompleted } = useTourStore();

  const handleStart = () => {
    if (!tourConfig) return;
    setOpen(false);
    // Small delay to let popover close before overlay appears
    setTimeout(() => startTour(tourConfig.id), 150);
  };

  const completed = tourConfig ? isTourCompleted(tourConfig.id) : false;

  const insignia =
    pendingCount > 0 ? (
      <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-2xs font-bold text-primary-foreground">
        {pendingCount}
      </span>
    ) : null;

  const contenido = (
    <PopoverContent className="w-72 p-0" side="bottom" align="end" sideOffset={8}>
      {tourConfig ? (
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">{tourConfig.title}</h3>
            {completed && (
              <Badge variant="success" className="text-2xs">
                Completado
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            {tourConfig.steps.length} pasos disponibles. Elige el nivel de detalle:
          </p>

          {/* Level selector */}
          <div className="flex gap-1">
            {LEVELS.map((level) => (
              <button
                key={level.value}
                onClick={() => setLevel(level.value)}
                className={`flex-1 text-xs px-2 py-1.5 rounded-md border transition-colors ${
                  selectedLevel === level.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'border-border hover:bg-accent'
                }`}
              >
                {level.label}
              </button>
            ))}
          </div>

          {/* Level description */}
          <p className="text-xs text-muted-foreground">
            {LEVELS.find((l) => l.value === selectedLevel)?.desc}
          </p>

          {/* Step count for selected level */}
          <p className="text-xs text-muted-foreground">
            {
              tourConfig.steps.filter((s) =>
                selectedLevel === 'avanzado'
                  ? true
                  : selectedLevel === 'intermedio'
                    ? s.levels.includes('basico') || s.levels.includes('intermedio')
                    : s.levels.includes('basico')
              ).length
            }{' '}
            pasos en nivel {selectedLevel}
          </p>

          <Button size="sm" className="w-full" onClick={handleStart}>
            {completed ? (
              <>
                <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                Repetir recorrido
              </>
            ) : (
              <>
                <Play className="w-3.5 h-3.5 mr-1.5" />
                Iniciar recorrido
              </>
            )}
          </Button>
        </div>
      ) : (
        <div className="p-4 text-center">
          <p className="text-sm text-muted-foreground">No hay recorrido guiado para esta página</p>
        </div>
      )}
    </PopoverContent>
  );

  if (isActive) return null;

  return (
    <EnLaRanuraDeAyuda
      enLaBarra={
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="relative h-10 w-10 rounded-full"
              aria-label={
                pendingCount > 0
                  ? `Recorrido guiado: ${pendingCount} pasos sin ver`
                  : 'Recorrido guiado'
              }
              title="Recorrido guiado"
            >
              <HelpCircle className="h-5 w-5" />
              {insignia}
            </Button>
          </PopoverTrigger>
          {contenido}
        </Popover>
      }
      sinBarra={
        <AnimatePresence>
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            className="fixed bottom-36 right-6 z-50"
          >
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  className="relative flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-shadow hover:shadow-xl"
                  aria-label="Recorrido guiado"
                >
                  <HelpCircle className="h-6 w-6" />
                  {insignia}
                </button>
              </PopoverTrigger>
              {contenido}
            </Popover>
          </motion.div>
        </AnimatePresence>
      }
    />
  );
}
